import { Icon } from "~/common/components/icon/Icon";
import { IconType } from "~/common/components/icon/IconType";
import { useAddProductToWishlistFlow } from "~/features/wishlist/hooks/useAddProductToWishlistFlow";
import { useDispatch } from "react-redux";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import { removeFromComparison } from "~/features/compareProducts/redux/actions";
import { IconSize } from "~/common/components/icon/IconSize";
import { userIsRegistered } from "~/domains/user/utils/roles";
import { isProductPMA } from "~/domains/product/utils/productDescriptorUtils";
import { Box, Button, IconButton, Stack, styled } from "~/common/mui";
import { AddToCartService } from "~/features/addToCart/AddToCartService";
import { canSeeAddToCartButton } from "~/utils/features";
import { useTranslation } from "react-i18next";
import { SaleTag } from "~/domains/salesCampaign/components/SaleTag/SaleTag";
import classNames from "classnames";
import { SideButton } from "~/common/components/Button/SideButton";
import { usePCCardLogic } from "~/features/compareProducts/components/ProductComparisonCard";
import { PriceElements, type PriceProps } from "~/domains/pricing/components/Price/PriceV4";
import { ProductDescriptorContext } from "~/domains/product/ProductContext";
import ProductImage from "~/domains/product/components/ProductImage";
import { Product } from "~/domains/product/Product";
import { useSalesHelper } from "~/domains/salesCampaign/utils/salesHelper";
import { SaleDiscountIndicator } from "~/domains/salesCampaign/components/SaleDiscountIndicator/SaleDiscountIndicator";

const WIDTHS = {
  product: "120px",
  gap: "10px",
  brand: "32px",
  manufacturer: "32px",
};
interface Props {
  productCode: string;
}

const ProductComparisonSmallCard = ({ productCode }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const {
    price,
    oldPrice,
    netPrice,
    discountFormatted,
    productDescriptor,
    packCode,
    showListPrice,
    listPrice,
    tariffSurcharge,
    hasSurcharge,
  } = usePCCardLogic(productCode);

  const wishlistButtonVisible = userHasPermission(Permission.ADD_TO_WISHLIST);
  const atcButtonVisible = canSeeAddToCartButton();

  const handleCloseClick = () => {
    dispatch(removeFromComparison(productCode, productDescriptor.base.designation));
  };

  const doAddToCart = () => {
    AddToCartService.openAddToCartWizard(
      {
        productCode: productDescriptor.base.code,
        restrictionQuery: "",
      },
      {
        page: "COMPARISON",
      },
    );
  };

  const [openAddToWishlist, addToWishlistModal] = useAddProductToWishlistFlow({ productInput: productDescriptor.base });
  const onWishlistBtnClick = () => {
    openAddToWishlist();
  };

  const salesHelper = useSalesHelper(productDescriptor);
  const isProductOnSale = salesHelper.isProductOnSale();
  const isPackageOnSale = salesHelper.isPackageOnSale(packCode);

  const disableWS = window.SCHA.config.disableAllWS;

  const showPrices = !disableWS && userHasPermission(Permission.PRICE);

  let atcButton;
  let atwButton;

  if (atcButtonVisible) {
    if (isProductPMA(productDescriptor)) {
      atcButton = (
        <div className="scha__comparison-card__on-request-only" onClick={doAddToCart}>
          <Icon type={IconType.InformationCircle} size={IconSize.FONT_24} className={"icon-margin"} />
          <span>{t("product.pma.messages.short")}</span>
        </div>
      );
    } else {
      atcButton = (
        <Button
          size={"medium"}
          startIcon={<Icon type={IconType.Cart} />}
          disabled={disableWS}
          onClick={doAddToCart}
          fullWidth
        >
          {userIsRegistered() ? t("addToCartPopup.add.submitBtn") : t("basket.addToBasket")}
        </Button>
      );
    }
  }

  if (wishlistButtonVisible) {
    if (atcButton) {
      atwButton = (
        <SideButton
          size={"small"}
          icon={IconType.Bookmark}
          color={"secondary"}
          onClick={onWishlistBtnClick}
          className={classNames("scha__comparison-page__add-to-wishlist")}
        />
      );
    } else {
      atwButton = (
        <Button startIcon={<Icon type={IconType.Bookmark} />} onClick={onWishlistBtnClick} fullWidth>
          {t("page.search.tabular.action.addToWishlist")}
        </Button>
      );
    }
  }

  return (
    <>
      {addToWishlistModal}
      <ProductDescriptorContext value={productDescriptor}>
        <div className="scha__comparison-small-card">
          <IconButton className="close-icon" color={"primary"} onClick={handleCloseClick}>
            <Icon type={IconType.Close} />
          </IconButton>
          {isProductOnSale && <FloatingSalesTag size={32} />}

          <Stack gap={2} sx={{ height: "100%" }}>
            <div className="product-image">
              <ProductImage product={productDescriptor.base} widths={WIDTHS} />
            </div>

            <Stack gap={0.5}>
              <Stack direction="row" alignItems="center">
                <Product.PreferredIndicator />
                <Product.PdpLink>
                  <Product.DesignationV2 variant="titleLarge" />
                </Product.PdpLink>
              </Stack>

              <Product.MMDChip />

              <Product.ShortDescriptionV2 variant="bodyLarge" />
            </Stack>

            {showPrices && (
              <>
                <PriceElements.Stack>
                  <Stack direction="row" gap={1} alignItems={"center"}>
                    <CurrentPrice
                      price={price}
                      zeroText={t("product.priceOnRequest")}
                      tariffSurcharge={tariffSurcharge}
                      variant="headlineMedium"
                      fontWeight={"bold"}
                      surchargeEnabled={hasSurcharge}
                    />
                    {isPackageOnSale && (
                      <SaleDiscountIndicator.V2
                        discountFormatted={discountFormatted}
                        variant="headlineSmall"
                        sx={{ fontWeight: 400 }}
                      />
                    )}
                  </Stack>
                  {oldPrice && <OldPrice price={oldPrice} />}
                  <PriceElements.NetPriceLabel price={netPrice} />
                </PriceElements.Stack>

                {showListPrice && listPrice && (
                  <Box>
                    <PriceElements.Price price={listPrice} />
                    <PriceElements.ListPriceLabel unit={netPrice?.unit ?? 1} />
                  </Box>
                )}
              </>
            )}

            {window.SCHA.feature.comparisonAvailability && <Product.StockIndicator />}

            <Stack direction="row" gap={1} sx={{ mt: "auto" }}>
              {atwButton}
              {atcButton}
            </Stack>
          </Stack>
        </div>
      </ProductDescriptorContext>
    </>
  );
};

export default ProductComparisonSmallCard;

const FloatingSalesTag = styled(SaleTag)({
  position: "absolute",
  top: 0,
  left: 0,
});

const CurrentPrice = styled(PriceElements.Price, {
  shouldForwardProp: (prop) => prop !== "soldOut",
})<PriceProps & { soldOut?: boolean }>(({ soldOut, theme }) => ({
  fontWeight: soldOut ? "normal" : "bold",
  color: soldOut ? theme.palette.text.secondary : theme.palette.text.primary,
  textDecoration: soldOut ? "line-through" : "none",
}));

const OldPrice = styled(PriceElements.Price, {
  shouldForwardProp: (prop) => prop !== "soldOut",
})<PriceProps & { soldOut?: boolean }>(({ soldOut, theme }) => ({
  color: soldOut ? theme.palette.text.primary : theme.palette.text.secondary,
  fontWeight: soldOut ? "bold" : "normal",
  textDecoration: soldOut ? "none" : "line-through",
}));
